import type { ReactNode } from "react";
import { useT } from "../i18n/index.tsx";
import type { IconKind, StatusView, Tone } from "../lib/status.ts";
import { ActivityIcon, AlertTriangleIcon, BanIcon, CheckIcon, CircleIcon, ClockIcon, GitBranchIcon, LoaderIcon, PauseIcon, PlayCircleIcon, SearchIcon, XIcon } from "./icons.tsx";

export function StatusIcon({ kind, size = 14 }: { kind: IconKind; size?: number }) {
  switch (kind) {
    case "check":
      return <CheckIcon size={size} strokeWidth={2.4} />;
    case "x":
      return <XIcon size={size} strokeWidth={2.4} />;
    case "alert":
      return <AlertTriangleIcon size={size} />;
    case "clock":
      return <ClockIcon size={size} />;
    case "spinner":
      return <LoaderIcon size={size} strokeWidth={2.4} className="status-spin" />;
    case "pause":
      return <PauseIcon size={size} strokeWidth={2.4} />;
    case "ban":
      return <BanIcon size={size} />;
    case "search":
      return <SearchIcon size={size} strokeWidth={2.2} data-icon="search" />;
    case "play":
      return <PlayCircleIcon size={size} strokeWidth={2.2} data-icon="play" />;
    case "activity":
      return <ActivityIcon size={size} strokeWidth={2.2} data-icon="activity" />;
    case "merge":
      return <GitBranchIcon size={size} strokeWidth={2.2} data-icon="merge" />;
    case "circle":
      return <CircleIcon size={size} />;
    case "dot":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="5" fill="currentColor" />
        </svg>
      );
  }
}

/** Status pill: icon + text, never color only (RF-UI-02). */
export function StatusPill({ view, suffix }: { view: StatusView; suffix?: ReactNode }) {
  const t = useT();
  return (
    <span className={`pill pill--${view.tone}`} data-tone={view.tone}>
      <StatusIcon kind={view.icon} />
      <span>{t(view.label)}</span>
      {suffix}
    </span>
  );
}

export function Pill({ tone, icon, children }: { tone: Tone; icon: IconKind; children: ReactNode }) {
  return (
    <span className={`pill pill--${tone}`} data-tone={tone}>
      <StatusIcon kind={icon} />
      <span>{children}</span>
    </span>
  );
}

/** Progress bar with an explicit text label (RF-UI-08) — the bar is never the only signal. */
export function ProgressBar({ value, max, label, detail }: { value: number; max: number; label: string; detail?: ReactNode }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="progress">
      <div
        className="progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={label}
        aria-label={label}
      >
        <div className="progress-fill" style={{ transform: `scaleX(${pct / 100})` }} />
      </div>
      <div className="progress-label">
        <span>{label}</span>
        {detail ? <span>{detail}</span> : null}
      </div>
    </div>
  );
}
